import { BrowserGameConnection } from './connection.js';
import { BrowserInput } from './input.js';
import { BasicAttackAnimations, renderAttackPoses } from './attack-animation.js';
import { GameRenderer } from './renderer.js';
import { preloadWeaponSkins } from './weapon-skins.js';
import { diagnosticProfileResolver, lazySkillAssets, preloadSkillAssets, remoteProfileResolver, type SkillVisuals } from './skill-visuals.js';
import { ELEMENT_SKILL_ASSET_CATALOG_V1 } from '../reference/skill-asset-element-catalog-v1.js';
import { DIAGNOSTIC_SKILL_ASSET_CATALOG_V1, DIAGNOSTIC_SKILL_VISUAL_PROFILE_V1 } from '../reference/skill-asset-diagnostic-fixture-v1.js';
import { renderMonsterRangeOverlay, selectMonsterAtPoint } from './monster-range-overlay.js';
import type { NetObjectId } from '../net/protocol.js';
import { DEFAULT_CAMERA_ZOOM, BASE_CAMERA_PIXELS_PER_METRE } from '../game/viewport-report.js';
import { basicAttackRangePreview } from './range-preview.js';
import { craftedSkillPreview } from './craft-footprint.js';
import { disableGoogleAutoSelect, mountGoogleSignIn } from './google-signin.js';
import { BrowserUi } from './ui.js';
import { SkillCraftingUi } from './skill-crafting-ui.js';
import { callApi, withStaleRetry } from './stale-retry.js';
import type { ApiResult, CharacterView, MeView, SkillVisualProfilesView } from '../game/http-types.js';

const canvas = document.getElementById('game') as HTMLCanvasElement | null;
if (!canvas) throw new Error('game canvas is missing');
// Skills resolve their profiles from the server's registry of equipped skills
// and draw element art. `?skillAssets=diagnostic` explicitly reads every
// profile as the placeholder fixture to check the asset runtime in a browser.
const reportRoleMismatch = (binding: { assetKey: string; role: string }, catalogued: string): void =>
  console.error(`skill asset ${binding.assetKey} is ${catalogued}, not ${binding.role}`);
const skillVisuals: SkillVisuals = new URLSearchParams(window.location.search).get('skillAssets') === 'diagnostic'
  ? { profiles: diagnosticProfileResolver(DIAGNOSTIC_SKILL_VISUAL_PROFILE_V1),
      assets: preloadSkillAssets(DIAGNOSTIC_SKILL_ASSET_CATALOG_V1, () => new Image(), reportRoleMismatch) }
  : { profiles: remoteProfileResolver({
        fetchProfiles: async () => {
          const response = await fetch('/api/skill-visual-profiles', { credentials: 'same-origin' });
          const result = await response.json() as ApiResult<SkillVisualProfilesView>;
          if (!response.ok || !result.ok) throw new Error('skill visual profiles unavailable');
          return result.value;
        },
        now: () => performance.now(),
      }),
      assets: lazySkillAssets(ELEMENT_SKILL_ASSET_CATALOG_V1, () => new Image(), reportRoleMismatch) };
const renderer = new GameRenderer(canvas, skillVisuals); const input = new BrowserInput();
renderer.setZoom(DEFAULT_CAMERA_ZOOM);
const animationContext = canvas.getContext('2d');
const weaponSkins = preloadWeaponSkins(() => new Image());
let attackAnimations: BasicAttackAnimations | undefined;
let selectedMonsterNetId: NetObjectId | undefined;
const animationWeaponIds = new Set([
  'DAGGER_PAIR', 'SWORD', 'HEAVY_AXE', 'SPEAR', 'BOW_REFERENCE',
  'ASSASSIN_BLADE', 'GREATSWORD', 'ARCANE_CATALYST', 'BLAST_CATALYST', 'RAPID_CATALYST',
]);
function animationGeometry(weaponId: string) {
  return animationWeaponIds.has(weaponId)
    ? basicAttackRangePreview(weaponId as Parameters<typeof basicAttackRangePreview>[0])
    : undefined;
}
const connection = new BrowserGameConnection({
  fetch: (url, init) => fetch(url, init), socket: (url) => new WebSocket(url),
  setTimeout: (callback, delay) => window.setTimeout(callback, delay), clearTimeout: (handle) => window.clearTimeout(handle as number),
  location: window.location,
});
let commandTimer: number | undefined; let windowFocused = document.hasFocus();
const ui = new BrowserUi((authenticated) => { if (authenticated) void startConnection(); else connection.stop(); });

const googleSlot = document.getElementById('google-signin');
if (googleSlot) void mountGoogleSignIn(googleSlot, async (credential) => {
  if (document.getElementById('auth')?.hidden) return;
  const challengeResponse = await fetch('/api/auth/google/challenge', { credentials: 'same-origin' });
  const challenge = await challengeResponse.json() as ApiResult<{ csrfToken: string }>;
  if (!challengeResponse.ok || !challenge.ok) throw new Error('Google 로그인 인증 준비 실패');
  if (document.getElementById('auth')?.hidden) return;
  const response = await fetch('/api/auth/google', {
    method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ credential, csrfToken: challenge.value.csrfToken }),
  });
  const result = await response.json() as ApiResult<MeView>;
  if (!response.ok || !result.ok) throw new Error('Google 로그인 인증 실패');
  if (document.getElementById('auth')?.hidden) return;
  ui.setMe(result.value);
  await startConnection();
}, (message) => ui.showError(message));
document.getElementById('logout')?.addEventListener('click', () => disableGoogleAutoSelect());

const currentRevision = async (): Promise<number> => (await callApi<MeView>('/api/me')).character.revision;

const crafting = new SkillCraftingUi({
  api: {
    options: () => callApi('/api/skills/options'),
    craft: (input) => withStaleRetry((revision) => callApi('/api/skills/craft', { ...input, revision }), input.revision, currentRevision),
    preview: (input) => callApi('/api/skills/craft/preview', input),
    statusMinimums: (input) => callApi('/api/skills/craft/status-minimums', input),
    me: () => callApi('/api/me'),
    equip: (input) => withStaleRetry((revision) => callApi('/api/skills/equip', { ...input, revision }), input.revision, currentRevision),
  },
  character: () => ui.currentCharacter() as CharacterView | undefined,
  sessionVersion: () => ui.currentSessionVersion(),
  onCharacter: (character) => ui.applyCharacter(character),
  onOpenChange: (open) => {
    // Opening takes the keyboard away from the game. Dropping held state is
    // the part that matters: a movement key or a charging skill still held
    // when the dialog opened would otherwise stay held behind it, and the
    // release would arrive as a cast the player never aimed.
    if (open) input.clear();
    synchronizeInput();
  },
});
ui.registerOverlay(() => crafting.isOpen());
ui.onSessionChanged(() => crafting.dismissForSessionChange());

function inputAllowed(): boolean {
  const active = document.activeElement;
  const typing = active instanceof HTMLInputElement || active instanceof HTMLSelectElement || active instanceof HTMLTextAreaElement;
  return connection.state === 'connected' && windowFocused && !document.hidden && !ui.isOverlayActive() && !typing;
}
function synchronizeSkillSlots(): void {
  const character = ui.currentCharacter();
  input.setAvailableSkillSlots(character?.equippedSkillIds.map((id) => id !== null) ?? [false, false, false, false]);
  input.setSkillAimModes(ui.skillAimModes());
}
/**
 * The same rest-area rule the server enforces, mirrored for the button.
 *
 * Advisory only: this decides whether the button looks available, and the
 * server checks again on every craft and equip. The rest area is the one the
 * server describes in its world view (`REST_AREA_RADIUS_M`).
 */
function synchronizeCrafting(): void {
  const local = connection.predicted?.localPlayer(); const rest = connection.world?.field.rest;
  const resting = local !== undefined && rest !== undefined && !local.down && Math.hypot(local.x - rest.x, local.y - rest.y) <= rest.radiusM;
  crafting.setRestAllowed(connection.state === 'connected' && resting);
}
function synchronizeInput(): void { synchronizeSkillSlots(); synchronizeCrafting(); input.setEnabled(inputAllowed()); }
function startCommands(): void {
  if (commandTimer !== undefined) window.clearInterval(commandTimer);
  const tickRate = connection.world?.tickRate ?? 20;
  commandTimer = window.setInterval(() => {
    synchronizeInput(); const local = connection.predicted?.localPlayer(); if (local) input.setPlayer(local);
    const latestAim = renderer.currentAim();
    if (latestAim) input.setAim(latestAim);
    // A cast carries the pointer only when its slot is pointer-aimed (`BrowserInput`); an automatic slot sends none.
    connection.sendInput(input.consumeFrame());
  }, 1_000 / tickRate);
}
async function startConnection(): Promise<void> { connection.stop(); attackAnimations = undefined; selectedMonsterNetId = undefined; await connection.start(); if (connection.me) ui.setMe(connection.me); if (connection.world) ui.setWorld(connection.world); startCommands(); }

connection.subscribe((event) => {
  if (event.type === 'state') {
    if (event.state !== 'connected') { attackAnimations?.clear(); selectedMonsterNetId = undefined; }
    ui.setConnection(event.state); synchronizeInput();
  }
  else if (event.type === 'error') ui.showError(event.message);
  else if (event.type === 'view') {
    const now = performance.now();
    attackAnimations ??= new BasicAttackAnimations(event.client.tickRate);
    attackAnimations.hydrate(event.client.renderObjects(undefined, now), now);
    ui.setPlayer(event.client.localPlayer(), event.client.serverTick); void ui.refresh();
  }
  else if (event.type === 'presentation') {
    const now = performance.now();
    const objects = connection.predicted?.renderObjects(undefined, now) ?? [];
    renderer.addEvents(event.events, objects, now);
    attackAnimations ??= new BasicAttackAnimations(connection.predicted?.tickRate ?? connection.world?.tickRate ?? 20);
    attackAnimations.accept(event.events, objects, now);
  }
});
window.addEventListener('keydown', (event) => { synchronizeSkillSlots(); if (inputAllowed()) { if (event.code === 'KeyB' && !event.repeat) ui.toggleRecall(); input.setKey(event.code, true); if (event.code.startsWith('Arrow') || event.code === 'Space') event.preventDefault(); } });
window.addEventListener('keyup', (event) => { synchronizeSkillSlots(); input.setKey(event.code, false); });
window.addEventListener('blur', () => { windowFocused = false; input.clear(); synchronizeInput(); });
window.addEventListener('focus', () => { windowFocused = true; synchronizeInput(); });
document.addEventListener('visibilitychange', () => { if (document.hidden) input.clear(); synchronizeInput(); });
document.addEventListener('focusin', synchronizeInput); document.addEventListener('focusout', () => window.setTimeout(synchronizeInput));
canvas.addEventListener('pointermove', (event) => { const bounds = canvas.getBoundingClientRect(); renderer.setAimScreen({ x: event.clientX - bounds.left, y: event.clientY - bounds.top }); });
canvas.addEventListener('pointerdown', (event) => {
  if (event.button !== 0 || !inputAllowed()) return;
  event.preventDefault();
  const bounds = canvas.getBoundingClientRect();
  renderer.setAimScreen({ x: event.clientX - bounds.left, y: event.clientY - bounds.top });
  const point = renderer.currentAim();
  selectedMonsterNetId = point ? selectMonsterAtPoint(connection.predicted?.renderObjects() ?? [], point)?.netId : undefined;
  if (typeof canvas.setPointerCapture === 'function') canvas.setPointerCapture(event.pointerId);
  input.setPrimaryAttackHeld(true);
});
const clearPrimaryAttack = (): void => { input.setPrimaryAttackHeld(false); };
canvas.addEventListener('pointerup', clearPrimaryAttack);
canvas.addEventListener('pointercancel', clearPrimaryAttack);
canvas.addEventListener('lostpointercapture', clearPrimaryAttack);
document.getElementById('auto-attack')?.addEventListener('change', (event) => input.setAutomaticBasicAttack((event.target as HTMLInputElement).checked));
document.getElementById('zoom')?.addEventListener('change', (event) => renderer.setZoom(Number((event.target as HTMLSelectElement).value)));
function drawFrame(): void {
  synchronizeSkillSlots();
  const character = ui.currentCharacter(); const heldSlot = input.heldSkillSlot();
  const equippedId = heldSlot === undefined ? undefined : character?.equippedSkillIds[heldSlot];
  const heldSkill = equippedId ? character?.skills.find((skill) => skill.id === equippedId) : undefined;
  const now = performance.now();
  renderer.render(connection.predicted, connection.world?.field, {
    ...(character ? {
      basic: basicAttackRangePreview(character.weaponId, character.stats.MAG),
      basicAimMode: input.automaticBasicAttackEnabled() ? 'AUTO_TARGET' as const : 'POINTER' as const,
    } : {}),
    // The held skill's footprint comes from the plan the server loads from the
    // same definition, composite or not.
    ...(heldSkill && character && heldSlot !== undefined
      ? { skill: craftedSkillPreview(heldSkill.definition, heldSkill.name, character.stats, character.weaponId), skillAimMode: input.skillAimMode(heldSlot) }
      : {}),
  }, now);
  // Rendering updated the CSS dimensions; reporting is deduplicated by the connection.
  // The server supplies the only trusted view center from the authoritative player.
  const viewport = renderer.currentViewport();
  connection.reportViewport({
    cssWidth: viewport.cssWidth, cssHeight: viewport.cssHeight,
    zoom: renderer.camera.pixelsPerMetre / BASE_CAMERA_PIXELS_PER_METRE,
  });
  if (animationContext) {
    const objects = connection.predicted?.renderObjects(undefined, now) ?? [];
    const selected = objects.find((object) => object.netId === selectedMonsterNetId);
    renderMonsterRangeOverlay(animationContext, selected?.kind === 'monster' ? selected : undefined,
      connection.predicted?.playerNetId, renderer.camera, renderer.currentViewport());
    if (attackAnimations) {
      const poses = attackAnimations.poses(objects, animationGeometry, now);
      renderAttackPoses(animationContext, poses, renderer.camera, renderer.currentViewport(), weaponSkins);
    }
  }
  ui.setPlayer(connection.predicted?.localPlayer(), connection.predicted?.serverTick);
}
// The next frame is requested before this one draws, so one frame that throws cannot stop drawing for the rest of the session.
let frameErrorReported = false;
function animate(): void {
  requestAnimationFrame(animate);
  try { drawFrame(); }
  catch (error) { if (!frameErrorReported) { frameErrorReported = true; console.error('frame failed', error); } }
}
requestAnimationFrame(animate); void startConnection();
